/**
 * Turning a thrown error into something a person can act on.
 *
 * The server answers a refused action with one long string: a headline, then a
 * newline-separated list of the specific lines at fault, then a closing
 * instruction. Dropped into `window.alert` that arrives as an unformatted wall
 * of text in a browser chrome dialog, with no way to tell "you must fix these
 * three lines" apart from "the database was unreachable".
 *
 * This module splits that message back into its parts and, separately, decides
 * whether the failure is the user's to fix (an operational refusal) or the
 * system's (a network drop, a 500, an expired session). The two need different
 * words and a different next step, so they are different tones.
 */

export type DialogTone =
  /** The action is refused until the user changes something. */
  | "error"
  /** A precondition in this screen is unmet — nothing was even attempted. */
  | "blocked"
  /** The system failed, not the input. Retrying is the sensible next step. */
  | "system"
  /** About to do something consequential; asking first. */
  | "confirm"
  /** It worked — here is what changed. */
  | "success";

export type DialogItem = {
  /** Short chip, e.g. "Line 3". */
  label?: string;
  /** The problem, or the value. */
  text: string;
  /** Secondary context, e.g. the item description. */
  note?: string;
};

export type DialogContent = {
  tone: DialogTone;
  title: string;
  summary?: string;
  itemsHeading?: string;
  items?: DialogItem[];
  /** What to do next. Rendered as its own block, not as another paragraph. */
  hint?: string;
  /** Raw message / status, kept behind a disclosure for support. */
  details?: string;
};

/**
 * "Line 3 (Chicken breast): Extension does not match" and its shorter
 * relatives, with or without a leading bullet, description, or issue text.
 */
const LINE_ROW =
  /^(?:[•\-*]\s*)?(line\s+[^:()]+?)\s*(?:\(([^)]*)\))?\s*(?::\s*(.*))?$/i;

function parseRow(raw: string): DialogItem {
  const line = raw.replace(/^[•\-*]\s*/, "").trim();
  const m = LINE_ROW.exec(line);
  if (m) {
    const label = m[1].trim();
    const note = (m[2] || "").trim();
    const text = (m[3] || "").trim();
    // "Line 7 (Ribeye)" with no issue after it — the row itself is the point
    // (the expiry gate lists lines, not faults), so the description carries it.
    if (!text) return { label, text: note || "Needs attention" };
    return { label, text, note: note || undefined };
  }
  return { text: line };
}

/**
 * Split a server message into headline / itemised faults / closing instruction.
 *
 * A single-line message has no structure to find, and is returned as the
 * summary untouched.
 */
export function splitServerMessage(message: string): {
  summary: string;
  items: DialogItem[];
  hint?: string;
} {
  const lines = String(message || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length <= 1) return { summary: lines[0] || "", items: [] };

  const summary = lines[0].replace(/:$/, "");
  const rest = lines.slice(1);

  // Everything after the headline is a fault. Nothing is popped off the end.
  //
  // This used to treat a trailing non-"Line …" row as the closing instruction,
  // because every refusal ended in one ("Correct these, save, and confirm
  // again."). Those instructions are gone — they are the prose that made
  // reviewers stop reading these dialogs — and with them gone the heuristic
  // became actively wrong: the reconciliation gate can end its list with a
  // whole-invoice flag ("SubTotal + Tax = 1125, but grand total shows 1200"),
  // which is a fault, and it was being rendered as the what-to-do-next block.
  //
  // Losing a real problem is a worse failure than losing a hint, and the
  // dialog does not need a hint: the title says what happened and the list
  // says what to fix.
  return { summary, items: rest.map(parseRow) };
}

/**
 * One plain string out of a thrown error, for an inline message (a page
 * section's own error line) rather than a structured dialog — `describeApiError`
 * below is for the dialog case. Several pages (Vendors, Clients, Settings) each
 * had their own copy of this exact parse; this is the one owner going forward.
 *
 * The server sometimes wraps its real message as JSON text inside `err.message`
 * (apiClient does this for some response shapes); unwrap it when present, and
 * fall back to the raw message otherwise. A message that isn't JSON at all is
 * the expected case, not a failure, so the empty catch is deliberate — there is
 * nothing to report, `msg` itself is already the right fallback.
 */
export function normalizeErrorMessage(err: any): string {
  const msg = err?.message || "";
  if (!msg) return "Request failed";
  try {
    const parsed = JSON.parse(msg);
    if (parsed?.message) return parsed.message;
  } catch {
    // Not JSON — msg is already the message to show.
  }
  return msg;
}

function isNetworkFailure(err: any): boolean {
  if (!err) return false;
  if (typeof err.status === "number") return false; // it reached the server
  const m = String(err.message || err).toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("load failed") ||
    m.includes("network request failed")
  );
}

export type DescribeOptions = {
  /** What the user was doing, e.g. "Confirm & sync to Inventory". */
  action: string;
  /** Shown when the error carries no usable message of its own. */
  fallback?: string;
};

/**
 * Build dialog content from a thrown API error.
 *
 * `status` is attached by apiClient. Its absence together with a fetch-shaped
 * message means the request never left the browser — worth saying plainly,
 * because "Failed to fetch" reads as an application bug to everyone who is not
 * a front-end developer.
 */
export function describeApiError(err: any, opts: DescribeOptions): DialogContent {
  const status: number | undefined =
    typeof err?.status === "number" ? err.status : undefined;
  const rawMessage = String(err?.message || "").trim();
  const detailBits = [
    status ? `HTTP ${status}` : null,
    rawMessage || null,
  ].filter(Boolean) as string[];
  const details = detailBits.join(" — ") || undefined;

  if (isNetworkFailure(err)) {
    return {
      tone: "system",
      title: `${opts.action} could not be sent`,
      summary:
        "The request never reached the server, so nothing was changed. This is " +
        "usually a dropped connection or a server that is not running.",
      hint: "Check your connection and try again. Nothing was half-applied.",
      details,
    };
  }

  if (status === 401 || status === 403) {
    return {
      tone: "system",
      title: "Not allowed to do this right now",
      summary:
        status === 401
          ? "Your session is no longer valid, so the server refused the request."
          : rawMessage || "Your account does not have permission for this action.",
      hint:
        status === 401
          ? "Sign in again, then retry — nothing was changed."
          : "Ask an administrator for the permission this action needs.",
      details,
    };
  }

  // 5xx, and anything that produced no readable message: the server broke, or
  // broke in a way it could not describe. Either way it is not the user's to fix.
  if ((status && status >= 500) || !rawMessage) {
    const parsed = rawMessage ? splitServerMessage(rawMessage) : null;
    return {
      tone: "system",
      title: `${opts.action} failed`,
      summary:
        parsed?.summary ||
        opts.fallback ||
        "The server hit an unexpected problem and could not complete the action.",
      itemsHeading: parsed?.items.length ? "What the server reported" : undefined,
      items: parsed?.items,
      hint:
        parsed?.hint ||
        "Try again in a moment. If it keeps failing, send this to support with the details below.",
      details,
    };
  }

  // Everything left is an operational refusal — the server understood, decided
  // not to, and said why in language meant for a person.
  const parsed = splitServerMessage(rawMessage);
  return {
    tone: "error",
    title: `${opts.action} was refused`,
    summary: parsed.summary,
    itemsHeading: parsed.items.length ? "What needs fixing" : undefined,
    items: parsed.items,
    hint: parsed.hint,
    details,
  };
}
